import { z } from 'zod'
import type { Hex } from '../../types'
import type { InternalTransactionWithIndex } from '../../types/internalTransaction'
import {
  addressPrimitiveSchema,
  bigintTextSchema,
  lowerHexSchema,
} from '../../types/primitive'
import type { HeadersInput } from '../types'
import type { BlockscoutV2Client } from './v2Client'

type GetInternalTransactionsByAddressHashV2Parameters = {
  client: BlockscoutV2Client
  address: Hex
  fromBlock?: number
  toBlock?: number
  headers?: HeadersInput
}

const addressHashSchema = z.object({
  hash: addressPrimitiveSchema,
})

const internalTransactionV2Schema = z.object({
  block_number: z.number(),
  created_contract: addressHashSchema.nullable(),
  from: addressHashSchema,
  gas_limit: bigintTextSchema,
  success: z.boolean(),
  timestamp: z.string().datetime(),
  to: addressHashSchema.nullable(),
  transaction_hash: lowerHexSchema,
  value: bigintTextSchema,
})

const nextPageParamsValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
])

const nextPageParamsSchema = z.record(nextPageParamsValueSchema)

const internalTransactionsPageSchema = z.object({
  items: z.array(internalTransactionV2Schema),
  next_page_params: nextPageParamsSchema.nullable(),
})

type NextPageParams = z.infer<typeof nextPageParamsSchema>

/**
 * Blockscout V2 API の次ページパラメータを query string 用に変換する。
 *
 * @param params.nextPageParams - Blockscout V2 API から返された次ページパラメータ
 * @returns null を除いた query string 用パラメータ
 */
const createNextPageQuery = ({
  nextPageParams,
}: {
  nextPageParams: NextPageParams
}): Record<string, string> => {
  const query: Record<string, string> = {}
  for (const [key, value] of Object.entries(nextPageParams)) {
    if (value !== null) {
      query[key] = value.toString()
    }
  }
  return query
}

/**
 * 指定 block 範囲に含まれる internal transaction かを判定する。
 *
 * @param params.transaction - 判定対象の internal transaction
 * @param params.fromBlock - 取得開始 block number
 * @param params.toBlock - 取得終了 block number
 * @returns 指定 block 範囲内なら true
 */
const isInBlockRange = ({
  transaction,
  fromBlock,
  toBlock,
}: {
  transaction: InternalTransactionWithIndex
  fromBlock?: number
  toBlock?: number
}): boolean => {
  if (fromBlock !== undefined && transaction.blockNumber < fromBlock) {
    return false
  }
  if (toBlock !== undefined && transaction.blockNumber > toBlock) {
    return false
  }
  return true
}

/**
 * Blockscout V2 API から address の internal transaction 一覧を取得する。
 *
 * @param params.client - Blockscout V2 API client
 * @param params.address - 取得対象 address
 * @param params.fromBlock - 取得開始 block number
 * @param params.toBlock - 取得終了 block number
 * @param params.headers - API request headers
 * @returns internal transaction 一覧
 */
export const getInternalTransactionsByAddressHashV2 = async ({
  client,
  address,
  fromBlock,
  toBlock,
  headers,
}: GetInternalTransactionsByAddressHashV2Parameters): Promise<
  InternalTransactionWithIndex[]
> => {
  const transactions: InternalTransactionWithIndex[] = []
  let query: Record<string, string> = {}
  const cursors = new Set<string>()

  while (true) {
    const page = await client.get({
      path: `/api/v2/addresses/${address}/internal-transactions`,
      query,
      resultSchema: internalTransactionsPageSchema,
      headers: typeof headers === 'function' ? await headers() : headers,
    })

    for (const item of page.items) {
      const transaction = {
        blockNumber: item.block_number,
        contractAddress: item.created_contract?.hash,
        from: item.from.hash,
        gas: item.gas_limit,
        isError: !item.success,
        timestamp: Date.parse(item.timestamp),
        to: item.to?.hash,
        txHash: item.transaction_hash,
        value: item.value,
      }
      if (isInBlockRange({ transaction, fromBlock, toBlock })) {
        transactions.push(transaction)
      }
    }

    if (page.next_page_params === null) {
      break
    }
    query = createNextPageQuery({ nextPageParams: page.next_page_params })
    const cursor = JSON.stringify(
      Object.entries(query).sort(([a], [b]) => a.localeCompare(b)),
    )
    if (cursors.has(cursor))
      throw new Error('Blockscout V2 pagination cursor did not advance')
    cursors.add(cursor)
  }

  return transactions
}
